import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import {
  useBoletimPreview,
  useContatosAgente,
  useEnviarTeste,
  type ContatoAgente,
  type ResultadoTesteAgente,
} from "@/hooks/use-agent";
import { HistoricoEnvios, ROTULO_CANAL } from "./historico-envios";
import { formatarPeriodo, periodoDeUmDia, periodoSemanal, type TipoBoletim } from "@shared/boletim-periodo";
import { ontemNaFazenda } from "@/lib/data-fazenda";
import { Copy, FileText, Loader2, Send } from "lucide-react";

// Aba Boletim de /agente — a PRÉVIA (Ciclo 1), o "Enviar teste" a partir dela
// (Ciclo 3a — validar formatação no canal real sem esperar as 06:00) e, embaixo,
// o histórico de envios com o botão de envio manual (Ciclo 2). Destinatários e
// agenda ficam nas abas Contatos e Envio automático.
//
// Desde 08/2026 a prévia tem DOIS tipos: Diário (um dia) e Semanal (o
// consolidado que sai no sábado). No semanal o campo de data continua sendo um
// dia — qualquer dia da semana desejada —, e a legenda mostra a seg–sex que ele
// resolveu. É a mesma função (`periodoSemanal`) que o servidor usa, para que a
// legenda nunca discorde do período que o boletim vai cobrir.
//
// O texto sai em <pre>: o boletim é feito de linhas, e qualquer renderização
// "bonitinha" aqui esconderia justamente o que precisa ser validado — como ele
// vai chegar no email e, depois, no WhatsApp. `whitespace-pre-wrap` +
// `break-words` pra caber no celular sem scroll horizontal.

type CanalTeste = "email" | "whatsapp_gzappy";

/** O período que a data escolhida cobre — um dia no diário, a seg–sex no semanal. */
function periodoDa(data: string, tipo: TipoBoletim) {
  return tipo === "semanal" ? periodoSemanal(data) : periodoDeUmDia(data);
}

interface CanalDisponivel {
  canal: CanalTeste;
  destino: string;
}

/** Só os canais que o contato de fato tem cadastrado — nunca oferecer um canal sem destino. */
function canaisDoContato(contato: ContatoAgente | undefined): CanalDisponivel[] {
  if (!contato) return [];
  const canais: CanalDisponivel[] = [];
  if (contato.email) canais.push({ canal: "email", destino: contato.email });
  if (contato.telefone) canais.push({ canal: "whatsapp_gzappy", destino: contato.telefone });
  return canais;
}

function EnviarTesteDialog(
  { data, tipo, aberto, onFechar }: { data: string; tipo: TipoBoletim; aberto: boolean; onFechar: () => void },
) {
  const { toast } = useToast();
  const { user } = useAuth();
  const contatos = useContatosAgente();
  const enviarTeste = useEnviarTeste();

  const [contatoId, setContatoId] = useState<string | null>(null);
  const [canal, setCanal] = useState<CanalTeste | null>(null);
  const [resultado, setResultado] = useState<ResultadoTesteAgente | null>(null);

  // Sempre que o diálogo abre, recomeça do zero — inclusive o default pro
  // próprio usuário logado, se ele estiver cadastrado como contato. É o caso
  // comum e evita mandar pro time sem querer.
  //
  // `useEffect` e não o `onOpenChange` do Dialog: o diálogo é aberto de FORA
  // (o botão "Enviar teste" seta `aberto=true` diretamente, sem DialogTrigger),
  // então o Radix nunca chama `onOpenChange(true)` sozinho — só `(false)`,
  // quando o usuário fecha por fora (Esc / clique no backdrop).
  useEffect(() => {
    if (!aberto) return;
    setResultado(null);
    const lista = contatos.data ?? [];
    const meuEmail = user?.email ?? null;
    const proprio = meuEmail
      ? lista.find(c => c.email?.toLowerCase() === meuEmail.toLowerCase())
      : undefined;
    const escolhido = proprio ?? lista[0];
    setContatoId(escolhido?.id ?? null);
    const canaisDoEscolhido = canaisDoContato(escolhido);
    setCanal(canaisDoEscolhido[0]?.canal ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  // Mesmo rótulo em todo lugar do diálogo: "sex 14/08" ou "10/08 a 14/08".
  const rotulo = formatarPeriodo(periodoDa(data, tipo));
  const contatoSelecionado = (contatos.data ?? []).find(c => c.id === contatoId);
  const canaisDisponiveis = canaisDoContato(contatoSelecionado);
  const destinoResolvido = canaisDisponiveis.find(c => c.canal === canal)?.destino;
  const ehProprioUsuario = !!(user?.email && contatoSelecionado?.email?.toLowerCase() === user.email.toLowerCase());
  const podeEnviar = !!(contatoId && canal && destinoResolvido) && !enviarTeste.isPending;

  const handleEnviar = () => {
    if (!contatoId || !canal) return;
    enviarTeste.mutate(
      { contatoId, canal, data, tipo },
      {
        onSuccess: r => setResultado(r),
        onError: e => toast({
          title: "Não consegui enviar o teste",
          description: e instanceof Error ? e.message : String(e),
          variant: "destructive",
        }),
      },
    );
  };

  return (
    <Dialog open={aberto} onOpenChange={v => { if (!v) onFechar(); }}>
      <DialogContent data-testid="dialog-enviar-teste">
        <DialogHeader>
          <DialogTitle>Enviar teste</DialogTitle>
          <DialogDescription>
            Manda o boletim {tipo === "semanal" ? "semanal" : "diário"} de {rotulo} de verdade, pro contato e canal
            escolhidos abaixo.
          </DialogDescription>
        </DialogHeader>

        {resultado ? (
          <div className="space-y-3">
            {resultado.desfecho === "enviada" && (
              <p className="text-sm text-sucesso" data-testid="resultado-teste-enviada">
                Enviado para {resultado.destino}.
                {resultado.providerId && <span className="block text-xs text-muted-foreground mt-1">id: {resultado.providerId}</span>}
              </p>
            )}
            {resultado.desfecho === "pulado_vazio" && (
              <p className="text-sm text-atencao" data-testid="resultado-teste-vazio">
                {tipo === "semanal" ? "Essa semana" : "Esse dia"} não teve movimento registrado — nada foi enviado.
              </p>
            )}
            {(resultado.desfecho === "falhou" || resultado.desfecho === "ja_enviada") && (
              <p className="text-sm text-erro break-words" data-testid="resultado-teste-erro">
                Não consegui enviar: {resultado.erro ?? "erro desconhecido"}
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setResultado(null)} data-testid="btn-testar-outro">
                Testar outro
              </Button>
              <Button onClick={onFechar}>Fechar</Button>
            </DialogFooter>
          </div>
        ) : contatos.isLoading ? (
          <Skeleton className="h-24" />
        ) : contatos.isError ? (
          <p className="text-sm text-erro" data-testid="erro-contatos-teste">
            {(contatos.error as any)?.message ?? "Não foi possível carregar os contatos."}
          </p>
        ) : (contatos.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Cadastre um contato na aba <strong>Contatos</strong> antes de testar.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Contato</Label>
              <Select
                value={contatoId ?? undefined}
                onValueChange={v => {
                  setContatoId(v);
                  const novo = (contatos.data ?? []).find(c => c.id === v);
                  setCanal(canaisDoContato(novo)[0]?.canal ?? null);
                }}
              >
                <SelectTrigger data-testid="select-teste-contato"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(contatos.data ?? []).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {canaisDisponiveis.length === 0 ? (
              <p className="text-xs text-atencao">
                Esse contato não tem email nem telefone cadastrado — edite-o em Contatos primeiro.
              </p>
            ) : (
              <div className="space-y-1">
                <Label className="text-xs">Canal</Label>
                <Select value={canal ?? undefined} onValueChange={v => setCanal(v as CanalTeste)}>
                  <SelectTrigger data-testid="select-teste-canal"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {canaisDisponiveis.map(c => (
                      <SelectItem key={c.canal} value={c.canal}>{ROTULO_CANAL[c.canal]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {contatoSelecionado && canal && destinoResolvido && (
              <p className="text-sm rounded-md border p-2.5 bg-muted/40" data-testid="confirmacao-teste">
                Enviar o boletim de <strong>{rotulo}</strong> para{" "}
                <strong>{contatoSelecionado.nome}</strong> ({destinoResolvido}) pelo{" "}
                <strong>{ROTULO_CANAL[canal]}</strong>? É uma mensagem real.
                {!ehProprioUsuario && (
                  <span className="block mt-1.5 text-atencao font-medium">
                    ⚠️ Esse destino não é você — confira antes de enviar.
                  </span>
                )}
              </p>
            )}

            <DialogFooter>
              <Button variant="ghost" onClick={onFechar} disabled={enviarTeste.isPending}>Cancelar</Button>
              <Button onClick={handleEnviar} disabled={!podeEnviar} data-testid="btn-confirmar-teste">
                {enviarTeste.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                Enviar
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function BoletimAgente() {
  const { toast } = useToast();
  const [tipo, setTipo] = useState<TipoBoletim>("diario");
  const [data, setData] = useState(ontemNaFazenda());
  // O que foi de fato consultado — trocar o tipo ou a data sem clicar em "Gerar
  // prévia" não pode fazer o texto na tela dizer respeito a outro período.
  const [consultado, setConsultado] = useState<{ data: string; tipo: TipoBoletim } | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [testeAberto, setTesteAberto] = useState(false);

  const preview = useBoletimPreview(consultado?.data ?? "", consultado?.tipo ?? "diario", consultado !== null);

  // Legenda do período resolvido: no semanal, "Semana de 10/08 a 14/08" — a
  // conta é a mesma do servidor (`periodoSemanal` mora em shared/).
  const periodoEscolhido = periodoDa(data, tipo);

  const handleGerar = () => {
    setCopiado(false);
    if (consultado && consultado.data === data && consultado.tipo === tipo) {
      preview.refetch();
      return;
    }
    setConsultado({ data, tipo });
  };

  const handleCopiar = async () => {
    if (!preview.data) return;
    try {
      await navigator.clipboard.writeText(preview.data.texto);
      setCopiado(true);
      toast({ title: "Boletim copiado" });
    } catch (e) {
      toast({
        title: "Não consegui copiar",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {/* Sem hora cravada — o horário é escolhido por canal em Envio automático. */}
        {tipo === "semanal"
          ? "Prévia do consolidado semanal — o mesmo texto que sai no sábado, junto com o diário."
          : "Prévia do boletim de um dia — o mesmo texto que sai no horário escolhido em Envio automático."}{" "}
        Nada é enviado por aqui.
      </p>

      <div className="space-y-1">
        <Label className="text-xs">Boletim</Label>
        {/* Segmented control: duas opções, sempre uma escolhida — `value` vazio
            (o Radix devolve "" ao clicar no item já ativo) é ignorado de
            propósito, senão a tela ficaria sem tipo nenhum selecionado. */}
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={tipo}
          onValueChange={v => { if (v) setTipo(v as TipoBoletim); }}
          className="justify-start"
        >
          <ToggleGroupItem value="diario" data-testid="toggle-boletim-diario">Diário</ToggleGroupItem>
          <ToggleGroupItem value="semanal" data-testid="toggle-boletim-semanal">Semanal</ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor="boletim-data" className="text-xs">
            {tipo === "semanal" ? "Dia da semana" : "Dia"}
          </Label>
          <Input
            id="boletim-data"
            type="date"
            value={data}
            onChange={e => setData(e.target.value)}
            className="w-44"
            data-testid="input-boletim-data"
          />
        </div>
        <Button onClick={handleGerar} disabled={!data || preview.isFetching} data-testid="btn-gerar-previa">
          {preview.isFetching ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}
          Gerar prévia
        </Button>
        {preview.data && (
          <>
            <Button variant="outline" onClick={handleCopiar} data-testid="btn-copiar-boletim">
              <Copy className="h-4 w-4 mr-1.5" />
              {copiado ? "Copiado!" : "Copiar"}
            </Button>
            <Button variant="outline" onClick={() => setTesteAberto(true)} data-testid="btn-enviar-teste">
              <Send className="h-4 w-4 mr-1.5" />
              Enviar teste
            </Button>
          </>
        )}
      </div>

      {tipo === "semanal" && (
        <p className="text-xs text-muted-foreground" data-testid="periodo-semanal-resolvido">
          Semana de <strong>{formatarPeriodo(periodoEscolhido)}</strong> — segunda a sexta. Sábado e domingo ficam de
          fora: a fazenda não registra movimento no fim de semana.
        </p>
      )}

      {consultado === null ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Escolha o dia e toque em <strong>Gerar prévia</strong>.
        </p>
      ) : preview.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-5" />)}
        </div>
      ) : preview.isError ? (
        // isError NUNCA pode virar "boletim vazio": um 403 (sem permissão) ou um
        // 500 apareceria como um dia sem movimento, que é o pior erro possível aqui.
        <p className="text-sm text-erro py-8 text-center" data-testid="erro-boletim">
          {(preview.error as any)?.message ?? "Não foi possível montar o boletim."}
        </p>
      ) : preview.data ? (
        <div className="space-y-2">
          {/* De que período é o texto abaixo. O selector pode ter mudado depois
              da última prévia — sem esta linha, o parágrafo do topo diria
              "consolidado semanal" enquanto o <pre> ainda mostra o dia. */}
          <p className="text-xs text-muted-foreground" data-testid="periodo-da-previa">
            {consultado?.tipo === "semanal" ? "Semana de " : "Boletim de "}
            <strong>{formatarPeriodo(preview.data.periodo)}</strong>
          </p>
          {preview.data.vazio && (
            <p className="text-xs text-atencao">
              Nenhum bloco teve movimento {consultado?.tipo === "semanal" ? "nesta semana" : "neste dia"} — um período
              assim não vira email (fica como "sem movimento" no histórico).
            </p>
          )}
          <pre
            className="rounded-md border bg-muted/40 p-3 text-sm whitespace-pre-wrap break-words font-mono leading-relaxed"
            data-testid="texto-boletim"
          >
            {preview.data.texto}
          </pre>
        </div>
      ) : null}

      {consultado && (
        <EnviarTesteDialog
          data={consultado.data}
          tipo={consultado.tipo}
          aberto={testeAberto}
          onFechar={() => setTesteAberto(false)}
        />
      )}

      <HistoricoEnvios />
    </div>
  );
}
